/**
 * AgentPipelineGraph — Real-time visualization of the multi-agent pipeline.
 *
 * Matches the OrchestratorPage light theme (white/violet/gray-50).
 * Shows each agent as a card with animated status, elapsed time,
 * progress detail, parallel platform branches, review score badge,
 * and the feedback-loop indicator.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Brain,
  PenTool,
  SplitSquareVertical,
  ShieldCheck,
  Clock,
  RotateCcw,
  Check,
  Loader2,
  AlertCircle,
  Pause,
  Zap,
  ArrowDown,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────

interface IterationRecord {
  iteration: number;
  score: number;
  notes: string[];
  action: string;
  timestamp?: string;
}

interface AgentPipelineGraphProps {
  stepsCompleted: string[];
  currentStep: string;
  status: string; // running | paused | completed | failed
  iterationCount: number;
  iterationHistory: IterationRecord[];
  targetPlatforms: string[];
  qualityScore: number;
  reviewMode: string; // auto | manual
}

type NodeStatus = 'pending' | 'running' | 'completed' | 'failed' | 'retrying';

// ── Agent node config ──────────────────────────────────────────────

const AGENT_NODES = [
  {
    id: 'strategy',
    label: 'Strategy Agent',
    desc: 'Analyzing topic, deciding angle & content type',
    icon: Brain,
    gradient: 'from-violet-500 to-purple-600',
    lightBg: 'bg-violet-50',
    lightBorder: 'border-violet-200',
    lightText: 'text-violet-700',
    iconColor: 'text-violet-600',
  },
  {
    id: 'content',
    label: 'Content Agent',
    desc: 'Writing captions, hooks & hashtags',
    icon: PenTool,
    gradient: 'from-cyan-500 to-blue-500',
    lightBg: 'bg-cyan-50',
    lightBorder: 'border-cyan-200',
    lightText: 'text-cyan-700',
    iconColor: 'text-cyan-600',
  },
  {
    id: 'optimization',
    label: 'Platform Optimizer',
    desc: 'Adapting content for each platform',
    icon: SplitSquareVertical,
    gradient: 'from-amber-500 to-orange-500',
    lightBg: 'bg-amber-50',
    lightBorder: 'border-amber-200',
    lightText: 'text-amber-700',
    iconColor: 'text-amber-600',
  },
  {
    id: 'review',
    label: 'Review Agent',
    desc: 'Scoring quality & checking compliance',
    icon: ShieldCheck,
    gradient: 'from-emerald-500 to-green-500',
    lightBg: 'bg-emerald-50',
    lightBorder: 'border-emerald-200',
    lightText: 'text-emerald-700',
    iconColor: 'text-emerald-600',
  },
  {
    id: 'scheduling',
    label: 'Scheduling Agent',
    desc: 'Picking optimal post times & saving',
    icon: Clock,
    gradient: 'from-indigo-500 to-violet-500',
    lightBg: 'bg-indigo-50',
    lightBorder: 'border-indigo-200',
    lightText: 'text-indigo-700',
    iconColor: 'text-indigo-600',
  },
];

// ── Helpers ────────────────────────────────────────────────────────

function getNodeStatus(
  nodeId: string,
  stepsCompleted: string[],
  currentStep: string,
  pipelineStatus: string,
  iterationCount: number,
): NodeStatus {
  if (stepsCompleted.includes(nodeId)) {
    if (iterationCount > 0 && currentStep === nodeId && pipelineStatus === 'running') {
      return 'retrying';
    }
    return 'completed';
  }
  if (currentStep === nodeId && pipelineStatus === 'running') {
    return 'running';
  }
  if (currentStep === nodeId && pipelineStatus === 'failed') {
    return 'failed';
  }
  if (
    currentStep === 'review_feedback' &&
    nodeId === 'review' &&
    pipelineStatus === 'paused'
  ) {
    return 'failed';
  }
  return 'pending';
}

/** Simple elapsed-time ticker for the running node */
function useElapsed(isRunning: boolean): number {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isRunning) {
      setElapsed(0);
      return;
    }
    setElapsed(0);
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [isRunning]);

  return elapsed;
}

function fmtTime(s: number): string {
  if (s < 60) {
    return `${s}s`;
  }
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

// ── Component ──────────────────────────────────────────────────────

const AgentPipelineGraph: React.FC<AgentPipelineGraphProps> = ({
  stepsCompleted,
  currentStep,
  status,
  iterationCount,
  iterationHistory,
  targetPlatforms,
  qualityScore,
  reviewMode,
}) => {
  const { t } = useTranslation();
  const [historyOpen, setHistoryOpen] = useState(false);

  const nodeStatuses = useMemo(() => {
    const map: Record<string, NodeStatus> = {};
    for (const node of AGENT_NODES) {
      map[node.id] = getNodeStatus(
        node.id,
        stepsCompleted,
        currentStep,
        status,
        iterationCount,
      );
    }
    return map;
  }, [stepsCompleted, currentStep, status, iterationCount]);

  const runningNodeId = useMemo(
    () => AGENT_NODES.find((n) => nodeStatuses[n.id] === 'running' || nodeStatuses[n.id] === 'retrying')?.id || '',
    [nodeStatuses],
  );

  const elapsed = useElapsed(!!runningNodeId);

  const completedCount = AGENT_NODES.filter((n) => nodeStatuses[n.id] === 'completed').length;
  const progressPct = Math.round((completedCount / AGENT_NODES.length) * 100);

  const showLoop =
    iterationCount > 0 ||
    (nodeStatuses['review'] === 'completed' &&
      !stepsCompleted.includes('scheduling') &&
      qualityScore > 0 &&
      qualityScore < 5.0);

  return (
    <div className="w-full space-y-3">
      {/* ── Header with progress bar ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-violet-500" />
          <span className="text-sm font-bold text-gray-800">
            {t('orchestrator_agent_pipeline', 'Agent Pipeline')}
          </span>
          {iterationCount > 0 && (
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
              {t('orchestrator_iteration', 'Iteration')} {iterationCount + 1}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 font-medium">
            {completedCount}/{AGENT_NODES.length}
          </span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
              reviewMode === 'auto'
                ? 'bg-violet-100 text-violet-600'
                : 'bg-orange-100 text-orange-600'
            }`}
          >
            {reviewMode === 'auto'
              ? t('orchestrator_auto_loop', 'Auto Loop')
              : t('orchestrator_manual_review', 'Manual Review')}
          </span>
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500 transition-all duration-700 ease-out"
          style={{ width: `${status === 'running' ? Math.max(progressPct, 8) : progressPct}%` }}
        />
      </div>

      {/* ── Agent Node Cards ── */}
      <div className="relative space-y-0">
        {AGENT_NODES.map((node, idx) => {
          const ns = nodeStatuses[node.id];
          const Icon = node.icon;
          const isOpt = node.id === 'optimization';
          const isActive = ns === 'running' || ns === 'retrying';

          return (
            <React.Fragment key={node.id}>
              {/* ── Connector arrow ── */}
              {idx > 0 && (
                <div className="flex justify-center py-0.5">
                  <ArrowDown
                    className={`w-4 h-4 ${
                      nodeStatuses[AGENT_NODES[idx - 1].id] === 'completed'
                        ? 'text-emerald-400'
                        : 'text-gray-200'
                    }`}
                  />
                </div>
              )}

              {/* ── Node card ── */}
              <div
                className={`
                  relative flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-500
                  ${isActive ? `${node.lightBg} ${node.lightBorder} shadow-sm` : ''}
                  ${ns === 'completed' ? 'bg-emerald-50/60 border-emerald-200/60' : ''}
                  ${ns === 'failed' ? 'bg-red-50 border-red-200' : ''}
                  ${ns === 'pending' ? 'bg-gray-50/60 border-gray-100 opacity-50' : ''}
                `}
              >
                {/* Icon bubble */}
                <div
                  className={`
                    flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center
                    ${ns === 'completed' ? 'bg-emerald-100' : ''}
                    ${isActive ? `bg-gradient-to-br ${node.gradient} shadow-sm` : ''}
                    ${ns === 'failed' ? 'bg-red-100' : ''}
                    ${ns === 'pending' ? 'bg-gray-100' : ''}
                  `}
                >
                  {ns === 'running' ? (
                    <Loader2 className="w-4.5 h-4.5 text-white animate-spin" />
                  ) : ns === 'completed' ? (
                    <Check className="w-4.5 h-4.5 text-emerald-600" />
                  ) : ns === 'retrying' ? (
                    <RotateCcw className="w-4.5 h-4.5 text-white animate-spin" />
                  ) : ns === 'failed' ? (
                    <AlertCircle className="w-4.5 h-4.5 text-red-500" />
                  ) : (
                    <Icon className="w-4.5 h-4.5 text-gray-400" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm font-semibold ${
                        ns === 'completed'
                          ? 'text-emerald-700'
                          : isActive
                          ? node.lightText
                          : ns === 'failed'
                          ? 'text-red-700'
                          : 'text-gray-400'
                      }`}
                    >
                      {node.label}
                    </span>
                    {isActive && (
                      <span className={`text-xs font-medium ${node.lightText} animate-pulse`}>
                        {ns === 'retrying'
                          ? t('orchestrator_retrying', 'Retrying...')
                          : t('orchestrator_processing', 'Processing...')}
                      </span>
                    )}
                    {ns === 'completed' && (
                      <Check className="w-3.5 h-3.5 text-emerald-500" />
                    )}
                  </div>

                  {/* Description when active */}
                  {isActive && (
                    <p className="text-xs text-gray-400 mt-0.5">{node.desc}</p>
                  )}

                  {/* Platform branches for optimization */}
                  {isOpt && targetPlatforms.length > 0 && ns !== 'pending' && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {targetPlatforms.map((p) => (
                        <span
                          key={p}
                          className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                            ns === 'completed'
                              ? 'bg-emerald-100 text-emerald-600'
                              : isActive
                              ? 'bg-white/80 text-amber-700 border border-amber-200'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right side: elapsed time or score */}
                {isActive && elapsed > 0 && (
                  <span className="flex-shrink-0 text-xs text-gray-400 font-mono tabular-nums">
                    {fmtTime(elapsed)}
                  </span>
                )}
                {node.id === 'review' && qualityScore > 0 && (
                  <div
                    className={`flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-bold ${
                      qualityScore >= 7
                        ? 'bg-emerald-100 text-emerald-700'
                        : qualityScore >= 5
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {qualityScore.toFixed(1)}/10
                  </div>
                )}
              </div>

              {/* ── Feedback loop indicator ── */}
              {node.id === 'review' && showLoop && (
                <div className="flex items-center gap-2 ml-14 py-1.5">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
                    <RotateCcw className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-xs text-amber-700 font-semibold">
                      {t('orchestrator_feedback_loop', 'Feedback Loop')}
                    </span>
                    <span className="text-xs text-amber-500">
                      &rarr; {t('orchestrator_back_to_content', 'Content Agent')}
                    </span>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Iteration History (collapsible) ── */}
      {iterationHistory.length > 0 && (
        <div className="rounded-xl border border-gray-100 bg-gray-50/50 overflow-hidden">
          <button
            onClick={() => setHistoryOpen(!historyOpen)}
            className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
          >
            <span className="text-xs font-semibold text-gray-600">
              {t('orchestrator_iteration_history', 'Iteration History')}
              <span className="ml-1.5 text-gray-400 font-normal">
                ({iterationHistory.length})
              </span>
            </span>
            {historyOpen ? (
              <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
            )}
          </button>

          {historyOpen && (
            <div className="px-4 pb-3 space-y-2 border-t border-gray-100 pt-2">
              {iterationHistory.map((iter, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-mono text-xs font-bold">
                    {iter.iteration}
                  </span>
                  <span
                    className={`font-bold ${
                      iter.score >= 7
                        ? 'text-emerald-600'
                        : iter.score >= 5
                        ? 'text-amber-600'
                        : 'text-red-600'
                    }`}
                  >
                    {iter.score.toFixed(1)}
                  </span>
                  <span
                    className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      iter.action === 'auto_retry'
                        ? 'bg-amber-100 text-amber-700'
                        : iter.action === 'paused_for_human' ||
                          iter.action === 'paused_low_score'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {iter.action === 'auto_retry'
                      ? t('orchestrator_auto_retried', 'Auto-retried')
                      : iter.action === 'paused_for_human'
                      ? t('orchestrator_paused_human', 'Paused for review')
                      : iter.action === 'paused_low_score'
                      ? t('orchestrator_paused_low', 'Paused (low score)')
                      : iter.action === 'retries_exhausted'
                      ? t('orchestrator_retries_exhausted', 'Retries exhausted')
                      : iter.action}
                  </span>
                  {(iter.notes || []).length > 0 && (
                    <span className="text-gray-400 truncate">{iter.notes[0]}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Paused for human review ── */}
      {status === 'paused' && currentStep === 'review_feedback' && (
        <div className="p-3.5 rounded-xl bg-orange-50 border border-orange-200 flex items-start gap-3">
          <Pause className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-semibold text-orange-700">
              {t('orchestrator_awaiting_review', 'Awaiting Your Review')}
            </div>
            <div className="text-xs text-orange-500 mt-0.5">
              {t(
                'orchestrator_review_instructions',
                'The AI review flagged quality issues. Edit the content below and resume to continue.',
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentPipelineGraph;
